import React from "react";
import { Box } from "@mui/material";
import { useTheme } from "../../theme/ThemeContext";
import Card, { CardContentWrapper } from "../common/Card";
import { AppTypography } from "../common/Typography";
import { useStoreInfo } from "../../context/StoreInfoContext";
import { getPackDisplayImage } from "../../utils/images";
import { usePreserveStoreIdNavigate } from "./usePreserveStoreIdNavigate";

const ProductCard = ({ pack, product }) => {
  const theme = useTheme();
  const { storeInfo } = useStoreInfo();

  const item = pack || product;
  const { label, pack_image_url, pack_price, id } = item;

  const getPrice = () => {
    if (pack_price && pack_price > 0) return pack_price;
    if (item.selling_price && item.selling_price > 0) return item.selling_price;
    if (firstProduct?.selling_price && firstProduct.selling_price > 0)
      return firstProduct.selling_price;
    return null;
  };

  const navigate = usePreserveStoreIdNavigate();

  const firstProduct = item.products && item.products[0];

  const getProductDetailsLink = () => {
    let type = "product";
    if (id) {
      if (item.products?.[0]?.pack_id) {
        type = "pack";
      } else {
        type = "product";
      }
      return `/products/${id}?type=${type}`;
    } else if (firstProduct?.product_id) {
      if (firstProduct?.pack_id) {
        type = "pack";
      } else {
        type = "product";
      }
      return `/products/${firstProduct.product_id}?type=${type}`;
    }
    return "#";
  };

  const getImageUrl = () => {
    if (pack_image_url && pack_image_url.trim() !== "") return pack_image_url;
    if (firstProduct?.image_urls?.[0]) return firstProduct.image_urls[0];
    return "/default-image.png";
  };

  const getLabel = () => {
    if (label && label.trim() !== "") return label;
    if (firstProduct?.label && firstProduct.label.trim() !== "")
      return firstProduct.label;
    return "Unnamed Product";
  };

  const handleCardClick = () => {
    const link = getProductDetailsLink();
    if (link !== "#") navigate(link);
  };

  const isFeatured = item.is_featured || item.featured || false;

  const getDescription = () => {
    return item.description || "Hand-picked items from our collection";
  };

  return (
    <Card variant="product" sx={{ maxHeight: "500px", maxWidth: "350px" }}>
      <Box
        sx={{
          width: "100%",
          aspectRatio: "1/1.01",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // borderTopLeftRadius: 20,
          // borderTopRightRadius: 20,
          overflow: "hidden",
          backgroundColor: theme?.homepage_product_card_background,
          cursor: "pointer",
          position: "relative",
        }}
        onClick={handleCardClick}
      >
        {/* Featured Badge */}
        {isFeatured && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              backgroundColor: "#FF6B35",
              color: "white",
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "0.7rem",
              fontWeight: 500,
              zIndex: 1,
            }}
          >
            ★ Featured
          </Box>
        )}

        <img
          src={getPackDisplayImage(pack)}
          alt={getLabel()}
          style={{
            maxWidth: "90%",
            objectFit: "cover",
            width: "auto",
            margin: "0 auto",
            height: "90%",
          }}
        />
      </Box>

      <CardContentWrapper
        sx={{
          backgroundColor: theme?.homepage_product_name_background || "white",
          // borderBottomLeftRadius: 20,
          // borderBottomRightRadius: 20,
          padding: "1.5rem !important",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          height: "155px",
        }}
      >
        {/* Content Section */}
        <Box
          sx={{
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {/* Product Name - First Line */}
          <AppTypography
            variant="subtitle1"
            style={theme?.typography?.body}
            textAlign="left"
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              lineHeight: "1.2",
              color: "#000",
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              height: "20px",
            }}
            title={getLabel()}
          >
            {getLabel()}
          </AppTypography>

          {/* Price and Rating Row - Third Line */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "12px",
            }}
          >
            {/* Product Price */}
            {getPrice() ? (
              <AppTypography
                variant="subtitle2"
                style={theme?.typography?.body}
                textAlign="left"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.3rem",
                  color: "#000",
                }}
              >
                {String.fromCodePoint(storeInfo?.store?.currency_symbol || 0x20B9)}{getPrice()}
              </AppTypography>
            ) : (
              <AppTypography
                variant="subtitle2"
                style={theme?.typography?.body}
                textAlign="left"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.3rem",
                  color: "#000",
                }}
              >
                {String.fromCodePoint(storeInfo?.store?.currency_symbol || 0x20B9)}{'0'}
              </AppTypography>
            )}
          </Box>
        </Box>

        {/* Add to Cart Button - Bottom */}
        <Box sx={{ flex: "0 0 auto", marginTop: "auto" }}>
          <Box
            sx={{
              background: theme?.app_button_color || "#000000",
              color: "white",
              padding: "10px 16px",
              borderRadius: "15px",
              marginTop: "0.7rem",
              textAlign: "center",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "0.9rem",
              transition: "all 0.2s ease",
              "&:hover": {
                background: theme?.app_button_secondary_color || "#000000",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
          >
            Add to Cart
          </Box>
        </Box>
      </CardContentWrapper>
    </Card>
  );
};

export default ProductCard;
